import { useEffect, useState } from "react";
import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import PageHeader from "../../components/ui/PageHeader";
import PushNotifications from "../../components/ui/PushNotifications";
import { getDashboardData } from "../../service/DashboardService";
import { getLogOverview } from "../../service/LogService";
import { getQueueOverview, QueueOverview } from "../../service/QueueService";
import { useTranslation } from "react-i18next";

type Dashboard = {
  tenants: { total: number; active: number; new30: number };
  participants: { total: number; active30: number };
  breefs: { total: number; running: number; new30: number; avgLengthSeconds: number };
  participation: { invited30: number; completed30: number; rate30: number | null };
  communication: { sms30: number; email30: number; failed30: number };
};

const isSupport = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}").adminRole === "support";
  } catch {
    return false;
  }
};

const minutes = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Ét nøgletal: stort tal, kort tekst over, forklaring under */
const Metric = ({ label, value, note, to, open }: { label: string; value: string | number; note?: string; to?: string; open?: string }) => (
  <Paper variant="outlined" sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
    {note && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {note}
      </Typography>
    )}
    {to && (
      <Box sx={{ mt: "auto", pt: 1 }}>
        <Button component={Link} to={to} size="small" sx={{ px: 0 }}>
          {open}
        </Button>
      </Box>
    )}
  </Paper>
);

export default function Score() {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const [data, setData] = useState<Dashboard | null>(null);
  const [ops, setOps] = useState<any>(null);
  const [queues, setQueues] = useState<QueueOverview | null>(null);
  const support = isSupport();

  useEffect(() => {
    getDashboardData().then((response: any) => response && setData(response));
    // Driftstallene kommer fra loggen, som kun superadmin må se
    if (!support) getLogOverview(24, false, navigate).then((response: any) => response && setOps(response));
    if (!support) getQueueOverview().then((response) => response && setQueues(response));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const section = (title: string) => (
    <Typography variant="h3" sx={{ mt: 4, mb: 1.5 }}>
      {title}
    </Typography>
  );
  const open = t("home.open");

  return (
    <Layout>
      <PageHeader title={t("home.t1")} description={t("home.intro")} />

      {section(t("home.sectionBusiness"))}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Metric
            label={t("home.tenants")}
            value={data?.tenants.active ?? "-"}
            note={t("home.tenantsNote", { total: data?.tenants.total ?? 0, new: data?.tenants.new30 ?? 0 })}
            to="/manage-tenant"
            open={open}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric
            label={t("home.participants")}
            value={data?.participants.total ?? "-"}
            note={t("home.participantsNote", { active: data?.participants.active30 ?? 0 })}
            to="/manage-users"
            open={open}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric
            label={t("home.breefsRunning")}
            value={data?.breefs.running ?? "-"}
            note={t("home.breefsNote", { total: data?.breefs.total ?? 0, new: data?.breefs.new30 ?? 0 })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric
            label={t("home.completion")}
            value={data?.participation.rate30 != null ? `${data.participation.rate30} %` : "-"}
            note={t("home.completionNote", { completed: data?.participation.completed30 ?? 0, invited: data?.participation.invited30 ?? 0 })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric label={t("home.sms")} value={data?.communication.sms30 ?? "-"} note={t("home.last30")} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric label={t("home.email")} value={data?.communication.email30 ?? "-"} note={t("home.last30")} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric label={t("home.failedInvites")} value={data?.communication.failed30 ?? "-"} note={t("home.last30")} to="/logs?category=communication&level=error" open={open} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Metric label={t("home.breefLength")} value={data ? minutes(data.breefs.avgLengthSeconds) : "-"} note={t("home.breefLengthNote")} />
        </Grid>
      </Grid>

      {!support && (
        <>
          {section(t("home.sectionOperations"))}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Metric label={t("home.errors")} value={ops?.levels?.error ?? "-"} note={t("home.last24")} to="/logs?level=error" open={open} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric
                label={t("home.smsDelivery")}
                value={ops?.sms?.deliveryRate != null ? `${ops.sms.deliveryRate} %` : "-"}
                note={t("home.deliveryNote", { sent: ops?.sms?.sent ?? 0, failed: ops?.sms?.failed ?? 0 })}
                to="/logs?event=SMS_FAILED"
                open={open}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric
                label={t("home.mailDelivery")}
                value={ops?.email?.deliveryRate != null ? `${ops.email.deliveryRate} %` : "-"}
                note={t("home.deliveryNote", { sent: ops?.email?.sent ?? 0, failed: ops?.email?.failed ?? 0 })}
                to="/logs?event=EMAIL_FAILED"
                open={open}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric label={t("home.cron")} value={ops?.cron?.failed ?? "-"} note={t("home.cronNote", { finished: ops?.cron?.finished ?? 0 })} to="/logs?category=cron&level=error" open={open} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric
                label={t("home.queueMessages")}
                value={queues ? queues.messages.queued + queues.messages.sending : "-"}
                note={t("home.queueNote", { sent: queues?.messages.sent24h ?? 0, failed: queues?.messages.failed ?? 0 })}
                to="/queues?tab=messages"
                open={open}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric
                label={t("home.queueInvites")}
                value={queues ? queues.invites.queued + queues.invites.sending : "-"}
                note={t("home.queueNote", { sent: queues?.invites.sent24h ?? 0, failed: queues?.invites.failed ?? 0 })}
                to="/queues?tab=invites"
                open={open}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Metric
                label={t("home.queueVideos")}
                value={queues ? queues.videos.queued + queues.videos.processing : "-"}
                note={t("home.queueNote", { sent: queues?.videos.done24h ?? 0, failed: queues?.videos.failed ?? 0 })}
                to="/queues?tab=videos"
                open={open}
              />
            </Grid>
          </Grid>
          <PushNotifications />
          {ops?.alerts?.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("home.alerts")}
              </Typography>
              {ops.alerts.slice(0, 5).map((alert: any) => (
                <Box key={alert.id} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.5 }}>
                  <Chip size="small" color="warning" label={alert.event} />
                  <Typography variant="body2">{alert.message}</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </>
      )}
    </Layout>
  );
}
